import isMobile from "ismobilejs";
import ShowLess from "../../partials/ShowLess";
import styles from "./data-collection.module.scss";
import mapBostonSmall from "../../assets/images/map-boston-small.jpg";
import queenstown from "../../assets/images/queenstown.jpg";
import mapSingaporeSmall from "../../assets/images/map-singapore-small.jpg";
import holland from "../../assets/images/holland.jpg";
import cameraFOV from "../../assets/images/camera-fov.jpg";
import dataImg from "../../assets/images/data.png";

export const DataCollection = (): JSX.Element => (
  <ShowLess className={styles.wrapper + " text-justify pb-2em"}>
    <div className="content">
      <div className={styles.main}>
        <h3 className={styles.subtitle}>Scene planning</h3>
        <p className={`${styles.text} p-0 mb-2em`}>
          For the nuScenes dataset we collect approximately 15h of driving data
          in Boston and Singapore. For the full nuScenes dataset, we publish
          data from{" "}
          <a
            href="https://www.google.com/maps/place/Seaport+District,+Boston,+MA,+USA/@42.3465665,-71.045773,15z/data=!3m1!4b1!4m13!1m7!3m6!1s0x89e3707f1bcdbc93:0x10fcd688f6115d23!2sSeaport+Blvd,+Boston,+MA,+USA!3b1!8m2!3d42.3520417!4d-71.0459318!3m4!1s0x89e37a831865f8c5:0xad7eb0e04b6892d9!8m2!3d42.3469759!4d-71.0408619"
            target="_blank"
            rel="noreferrer"
          >
            Boston Seaport
          </a>{" "}
          and{" "}
          <a
            href="https://www.google.com/maps/place/One+North+Station/@1.2983401,103.785033,16z/data=!4m5!3m4!1s0x31da1a45527bd28b:0xc5a49606699d4811!8m2!3d1.299949!4d103.7874148"
            target="_blank"
            rel="noreferrer"
          >
            Singapore’s One North
          </a>
          ,{" "}
          <a
            href="https://www.google.com/maps/place/National+University+of+Singapore/@1.2975556,103.7749802,16z/data=!4m13!1m7!3m6!1s0x31da1a5234b05815:0x500f7acaedaa4b0!2sQueenstown!3b1!8m2!3d1.2941664!4d103.7861271!3m4!1s0x31da1a56784202d9:0x488d08d6c1f88d6b!8m2!3d1.2966435!4d103.7763941"
            target="_blank"
            rel="noreferrer"
          >
            Queenstown
          </a>{" "}
          and{" "}
          <a
            href="https://www.google.com/maps/place/Holland+Village+MRT+Station/@1.3040678,103.8008902,14z/data=!4m8!1m2!2m1!1sholland+village!3m4!1s0x31da1a6b703bb689:0xabdb7019b6ce5e9!8m2!3d1.3111457!4d103.7961408"
            target="_blank"
            rel="noreferrer"
          >
            Holland Village
          </a>{" "}
          districts. Driving routes are carefully chosen to capture challenging
          scenarios. We aim for a diverse set of locations, times and weather
          conditions. To balance the class frequency distribution, we include
          more scenes with rare classes (such as bicycles). Using these
          criteria, we manually select 1000 scenes of 20s duration each. These
          scenes are carefully annotated using human experts. The{" "}
          <a
            href="https://github.com/nutonomy/nuscenes-devkit/blob/master/docs/instructions_nuscenes.md"
            target="_blank"
            rel="noreferrer"
          >
            annotator instructions
          </a>{" "}
          can be found in the devkit repository.
        </p>
        <div className="flex mb-2em">
          <div className={styles.flex}>
            <img src={mapBostonSmall} alt="boston" className={styles.img} />
            <div className={styles.location}>Boston Seaport</div>
          </div>
          <div className={styles.flex}>
            <img src={queenstown} alt="queenstown" className={styles.img} />
            <div className={styles.location}>Singapore Queenstown</div>
          </div>
        </div>
        <div className="flex mb-2em">
          <div className={styles.flex}>
            <img
              src={mapSingaporeSmall}
              alt="singapore"
              className={styles.img}
            />
            <div className={styles.location}>Singapore One North</div>
          </div>
          <div className={styles.flex}>
            <img src={holland} alt="holland" className={styles.img} />
            <div className={styles.location}>Singapore Holland Village</div>
          </div>
        </div>
        <h3 className={styles.subtitle}>Car setup</h3>
        <p className={styles.setupText}>
          We use two{" "}
          <a href="https://en.wikipedia.org/wiki/Renault_Zoe">Renault Zoe</a>{" "}
          cars with an identical sensor layout to drive in Boston and Singapore.
          The data was gathered from a research platform and is not indicative
          of the setup used in Motional products. Please refer to the above
          figure for the placement of the sensors. We release data from the
          following sensors:
        </p>
        <img src={dataImg} alt="data" className={styles.dataImg} />
        <ul className={styles.lists}>
          <li className={styles.list + " " + styles.listP}>
            <p className={styles.padding}>
              1x spinning LIDAR (Velodyne HDL32E):
            </p>
            <ul>
              <li className={styles.setupTextNoPadding}>
                20Hz capture frequency
              </li>
              <li className={styles.setupTextNoPadding}>
                32 beams, 1080 (+-10) points per ring
              </li>
              <li className={styles.setupTextNoPadding}>32 channels</li>
              <li className={styles.setupTextNoPadding}>
                360° Horizontal FOV, +10° to -30° Vertical FOV, uniform azimuth
                angles
              </li>
              <li className={styles.setupTextNoPadding}>
                80m-100m Range, Usable returns up to 70 meters, ± 2 cm accuracy
              </li>
              <li className={styles.setupTextNoPadding}>
                Up to ~1.39 Million Points per Second
              </li>
            </ul>
          </li>
          <li className={styles.list + " " + styles.listP}>
            <p className={styles.padding}>
              5x long range RADAR sensor (Continental ARS 408-21):
            </p>
            <ul>
              <li className={styles.setupTextNoPadding}>
                13Hz capture frequency
              </li>
              <li className={styles.setupTextNoPadding}>77GHz</li>
              <li className={styles.setupTextNoPadding}>
                Independently measures distance and velocity in one cycle using
                Frequency Modulated Continuous Wave
              </li>
              <li className={styles.setupTextNoPadding}>Up to 250m distance</li>
              <li className={styles.setupTextNoPadding}>
                Velocity accuracy of ±0.1 km/h
              </li>
            </ul>
          </li>
          <li className={styles.list + " " + styles.listP}>
            <p className={styles.padding}>6x camera (Basler acA1600-60gc):</p>
            <ul>
              <li className={styles.setupTextNoPadding}>
                12Hz capture frequency
              </li>
              <li className={styles.setupTextNoPadding}>
                Evetar Lens N118B05518W F1.8 f5.5mm 1/1.8"
              </li>
              <li className={styles.setupTextNoPadding}>
                1/1.8'' CMOS sensor of 1600x1200 resolution
              </li>
              <li className={styles.setupTextNoPadding}>
                Bayer8 format for 1 byte per pixel encoding
              </li>
              <li className={styles.setupTextNoPadding}>
                1600x900 ROI is cropped from the original resolution to reduce
                processing and transmission bandwidth
              </li>
              <li className={styles.setupTextNoPadding}>
                Auto exposure with exposure time limited to the maximum of 20 ms
              </li>
              <li className={styles.setupTextNoPadding}>
                Images are unpacked to BGR format and compressed to JPEG
              </li>
              <li className={styles.setupTextNoPadding}>
                See camera orientation and overlap in the figure below.
              </li>
            </ul>
          </li>
          <li className={styles.list + " " + styles.listP}>
            <p className={styles.padding}>
              1x IMU & GPS (
              <a
                href="https://www.advancednavigation.com/solutions/spatial/"
                target="_blank"
                rel="noreferrer"
              >
                Advanced Navigation Spatial
              </a>
              ):
            </p>
            <ul>
              <li className={styles.setupTextNoPadding}>
                Position accuracy of 20mm
              </li>
              <li className={styles.setupTextNoPadding}>
                Heading accuracy of 0.2° with GNSS
              </li>
              <li className={styles.setupTextNoPadding}>
                Roll & pitch accuracy of 0.1°
              </li>
              <li className={styles.setupTextNoPadding}>
                Localization takes into account IMU, GPS and HD lidar maps (see{" "}
                <a
                  href="https://arxiv.org/abs/1903.11027"
                  target="_blank"
                  rel="noreferrer"
                >
                  our paper
                </a>{" "}
                for more details)
              </li>
            </ul>
          </li>
        </ul>
        <div className="text-center">
          <img src={cameraFOV} alt="camera" />
        </div>
        <h3 className={styles.subtitle}>Sensor calibration</h3>
        <p className={styles.setupText + " " + styles.width}>
          To achieve a high quality multi-sensor dataset, it is essential to
          calibrate the extrinsics and intrinsics of every sensor. We express
          extrinsic coordinates relative to the ego frame, i.e. the midpoint of
          the rear vehicle axle. The most relevant steps are described below:
        </p>
        <ul className={styles.lists}>
          <li className={styles.list + " " + styles.listP}>
            <p className={isMobile.any ? styles.listP : ""}>
              LIDAR extrinsics:
            </p>
            <p
              className={
                isMobile.any ? styles.mobileText : styles.setupTextNoPadding
              }
            >
              We use a laser liner to accurately measure the relative location
              of the LIDAR to the ego frame.
            </p>
          </li>
          <li className={styles.list + " " + styles.listP}>
            <p className={isMobile.any ? styles.list : ""}>
              Camera extrinsics:
            </p>
            <p
              className={
                isMobile.any ? styles.mobileText : styles.setupTextNoPadding
              }
            >
              We place a cube-shaped calibration target in front of the camera
              and LIDAR sensors. The calibration target consists of three
              orthogonal planes with known patterns. After detecting the
              patterns we compute the transformation matrix from camera to LIDAR
              by aligning the planes of the calibration target. Given the LIDAR
              to ego frame transformation computed above, we can then compute
              the camera to ego frame transformation and the resulting extrinsic
              parameters.
            </p>
          </li>
          <li className={styles.list + " " + styles.listP}>
            <p className={isMobile.any ? styles.list : ""}>RADAR extrinsics</p>
            <p
              className={
                isMobile.any ? styles.mobileText : styles.setupTextNoPadding
              }
            >
              We mount the radar in a horizontal position. Then we collect radar
              measurements by driving in an urban environment. After filtering
              radar returns for moving objects, we calibrate the yaw angle using
              a brute force approach to minimize the compensated range rates for
              static objects.
            </p>
          </li>
          <li className={styles.list + " " + styles.listP}>
            <p className={isMobile.any ? styles.list : ""}>
              Camera intrinsic calibration
            </p>
            <p
              className={
                isMobile.any ? styles.mobileText : styles.setupTextNoPadding
              }
            >
              We use a calibration target board with a known set of patterns to
              infer the intrinsic and distortion parameters of the camera.
            </p>
          </li>
        </ul>
        <h3 className={styles.title}>Sensor synchronization</h3>
        <p className={styles.setupText}>
          In order to achieve good cross-modality data alignment between the
          LIDAR and the cameras, the exposure of a camera is triggered when the
          top LIDAR sweeps across the center of the camera’s FOV. The timestamp
          of the image is the exposure trigger time; and the timestamp of the
          LIDAR scan is the time when the full rotation of the current LIDAR
          frame is achieved. Given that the camera’s exposure time is nearly
          instantaneous, this method generally yields good data alignment. Note
          that the cameras run at 12Hz while the LIDAR runs at 20Hz. The 12
          camera exposures are spread as evenly as possible across the 20 LIDAR
          scans, so not all LIDAR scans have a corresponding camera frame.
          Reducing the frame rate of the cameras to 12Hz helps to reduce the
          compute, bandwidth and storage requirement of the perception system.
        </p>
        <h3 className={styles.title}>Privacy protection</h3>
        <p className={styles.setupText}>
          It is our priority to protect the privacy of third parties. For this
          purpose we use state-of-the-art object detection techniques to detect
          license plates and faces. We aim for a high recall and remove false
          positives that do not overlap with the reprojections of the known
          person and car boxes. Eventually we use the output of the object
          detectors to blur faces and license plates in the images of nuScenes.
        </p>
      </div>
    </div>
  </ShowLess>
);

export default DataCollection;
